import { useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { LOCATION_DATA } from "@/lib/locationData";

export default function SupportedCities() {
  const navigate = useNavigate();
  const totalCities = LOCATION_DATA.reduce((s, r) => s + r.cities.length, 0);
  const totalCountries = LOCATION_DATA.length;

  // Group by region
  const regions = new Map<string, typeof LOCATION_DATA>();
  LOCATION_DATA.forEach(c => {
    const list = regions.get(c.region) || [];
    list.push(c);
    regions.set(c.region, list);
  });

  return (
    <div className="min-h-screen bg-background pb-16">
      <div className="border-b border-border bg-card">
        <div className="container py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-heading text-lg font-medium text-foreground">Supported Cities & Countries</h1>
            <p className="text-sm text-muted-foreground">{totalCities} cities across {totalCountries} countries with localized roadmap data</p>
          </div>
          <Badge variant="secondary" className="text-xs gap-1">
            <Globe className="w-3 h-3" /> {totalCountries} countries
          </Badge>
        </div>
      </div>

      <div className="container pt-6 max-w-4xl">
        <div className="mb-6 p-4 rounded-xl bg-primary/5 border border-primary/20">
          <p className="text-sm text-foreground">
            <strong>Each city includes:</strong> localized startup cost estimates, permit & license requirements,
            neighborhood data, rent benchmarks, labor cost multipliers, and currency-specific formatting.
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Don't see your city? You can still use Oplif — we'll apply the closest regional benchmark. We add new cities regularly.
          </p>
        </div>

        {Array.from(regions.entries()).map(([region, countries]) => (
          <div key={region} className="mb-8">
            <h2 className="font-heading text-lg font-medium text-foreground mb-3">{region}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {countries.map(country => (
                <Card key={country.country} className="border-border/60">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-2xl">{country.flag}</span>
                      <h3 className="font-heading font-medium text-foreground">{country.country}</h3>
                      <Badge variant="outline" className="text-[10px] ml-auto">
                        {country.cities.length} {country.cities.length === 1 ? "city" : "cities"}
                      </Badge>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {country.cities.map(city => (
                        <div
                          key={city.name}
                          className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-sm ${
                            city.comingSoon
                              ? "bg-muted/30 text-muted-foreground"
                              : "bg-muted/50 text-foreground"
                          }`}
                        >
                          <MapPin className="w-3 h-3 text-primary" />
                          {city.name}
                          {city.comingSoon && (
                            <Badge variant="outline" className="text-[8px] px-1 py-0 ml-0.5">Soon</Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
